import * as Sentry from '@sentry/nuxt'
import { initAmplitude } from '@brighterly/lib-core-functions/analytics'

import { getAmplitudeLink, getDeviceType } from '@shared/lib/amplitude'

import { useCustomerStore } from '../model/customer-store/store'
import { useDisciplineStore } from '../model/discipline-store/store'
import { useKidStore } from '../model/kid-store/store'

let started: Promise<string | null> | null = null

/**
 * The one Amplitude start per page load. Without a loaded customer (login page) the SDK generates the device id;
 * the lib cannot switch it later, so an SPA login keeps that id until the next page load (spec D10–D11).
 * `eventProps` is the only place the tracker props are written.
 */
export const startAnalytics = (): Promise<string | null> => {
  if (started) return started

  const config = useRuntimeConfig().public
  const customerStore = useCustomerStore()
  const kidStore = useKidStore()
  const disciplineStore = useDisciplineStore()
  const deviceType = getDeviceType()

  started = initAmplitude({
    apiKey: config.amplitudeApiKey,
    serverUrl: config.amplitudeServerUrl,
    deviceId: customerStore.customer?.device_id || undefined,
    userProps: { environment: config.environment || '', user_type: 'customer', brand: config.brand },
    eventProps: () => {
      const customer = customerStore.customer
      const kid = customer?.kids.find(item => item.id === kidStore.activeKidId)
      const discipline = disciplineStore.activeDiscipline

      return {
        customer_type: customer?.type ?? null,
        source_funnel: customer?.source_funnel ?? null,
        source_domain: window.location.hostname,
        device_type: deviceType,
        kid_id: kid?.id ?? null,
        discipline_id: discipline?.id ?? null,
        discipline_code: discipline?.code.toLowerCase() ?? null,
      }
    },
    debug: import.meta.dev,
  })

  void started.then((deviceId) => {
    if (deviceId && config.sentryDsn) {
      Sentry.setUser({ amplitude_url: getAmplitudeLink(deviceId) })
    }
  })

  return started
}
